import { useState } from 'react';
import { Button, Card, CardContent, Typography } from '@mui/material';
import { Column, Row } from 'further-ui/components/Layout';
import NewUserForm from './NewUserForm';
import Table, { Column as TableColumn } from 'further-ui/components/Table';
import useInvestorUsers from './useInvestorUsers';
import Avatar from 'further-ui/components/Avatar';
import { Cached, Delete } from '@mui/icons-material';
import StatusBadge from 'components/new/StatusBadge';
import BasicSelect from 'further-ui/components/BasicSelect';
import { getLpInvestorAccessPermissionGroupOptions } from 'further-ui/utils';
import { LpInvestorAccessPermissionGroupKey } from 'further-types/further-user';
import { useDisclosure } from 'further-ui/hooks';
import ConfirmDialog from 'further-ui/components/ConfirmDialog';
import { InvestorUser } from '../../types';
import useVehicleApplication from 'hooks/lp/data/vehicleApplication/useVehicleApplication';

type Props = {
  vehicleApplicationId: string;
  vehicleId: string;
};

const InvestorUsers: React.FC<Props> = ({
  vehicleApplicationId,
  vehicleId,
}) => {
  const { users, queryKey } = useVehicleApplication(
    vehicleId,
    vehicleApplicationId,
  );
  const hasUsers = !!users.length;
  const [showNewUserForm, setShowNewUserForm] = useState(!hasUsers);
  const removeUserDisclosure = useDisclosure<{ userId: string }>();

  const handleShowUserForm = () => {
    setShowNewUserForm(true);
  };

  const { isLoading, handleUpdateInvestorUser, handleRemoveUser } =
    useInvestorUsers({ vehicleApplicationQueryKey: queryKey });

  const handleChangeUserPermissions = (
    userId: string,
    permissionsGroup: LpInvestorAccessPermissionGroupKey,
  ) => {
    const user = users.find((user) => user.id === userId);
    if (!user) {
      return;
    }

    handleUpdateInvestorUser({
      vehicleApplicationId,
      vehicleId,
      userId,
      permissionsGroup,
      copyIntoSystemEmails: user.copyIntoSystemEmails,
      isDefaultUser: user.isDefaultUser,
    });
  };

  const handleChangeUserReceiveEmails = (
    userId: string,
    copyIntoSystemEmails: boolean,
  ) => {
    const user = users.find((user) => user.id === userId);
    if (!user) {
      return;
    }

    handleUpdateInvestorUser({
      vehicleApplicationId,
      vehicleId,
      userId,
      copyIntoSystemEmails,
      permissionsGroup: user.permissionsGroup,
      isDefaultUser: user.isDefaultUser,
    });
  };

  const handleChangeDefaultUser = (userId: string) => {
    const user = users.find((user) => user.id === userId);
    if (!user) {
      return;
    }

    handleUpdateInvestorUser({
      vehicleApplicationId,
      vehicleId,
      userId,
      permissionsGroup: user.permissionsGroup,
      copyIntoSystemEmails: user.copyIntoSystemEmails,
      isDefaultUser: true,
    });
  };

  const columns: Array<TableColumn<InvestorUser>> = [
    {
      key: 'user',
      label: 'User',
      render: (user) => (
        <Row spacing="xs" centered>
          <Avatar
            color="random"
            text={`${user.firstName} ${user.lastName}`}
            size="lg"
          />
          <div>
            <Typography variant="h6">
              {user.firstName ?? ''} {user.lastName ?? ''}
            </Typography>
            <Typography variant="body2">{user.email} </Typography>
          </div>
        </Row>
      ),
    },
    {
      key: 'permissions',
      label: 'Permissions',
      tooltip:
        "Select whether each user has: (i) full access to the investor's portal and profile (ii) full access but cannot sign documents or (iii) portal and profile view-only rights.",
      render: (user) => (
        <BasicSelect
          fullWidth
          size="small"
          value={user.permissionsGroup as string}
          disabled={isLoading}
          onChange={(permissionsGroup) => {
            handleChangeUserPermissions(
              user.id,
              permissionsGroup as LpInvestorAccessPermissionGroupKey,
            );
          }}
          options={getLpInvestorAccessPermissionGroupOptions()}
        />
      ),
    },
    {
      key: 'receiveEmails',
      label: 'Receive Emails',
      render: (user) => (
        <BasicSelect
          fullWidth
          size="small"
          value={user.copyIntoSystemEmails ? 1 : 0}
          disabled={isLoading}
          onChange={(value) => {
            handleChangeUserReceiveEmails(user.id, !!value);
          }}
          options={[
            { label: 'Yes', value: 1 },
            { label: 'No', value: 0 },
          ]}
        />
      ),
    },
    {
      key: 'defaultUser',
      label: 'Default User',
      render: (user) => (
        <StatusBadge color={user.isDefaultUser ? 'success' : 'error'} />
      ),
    },
  ];

  return (
    <Card variant="outlined">
      <CardContent>
        <Column fullWidth spacing="lg">
          <Row centered justify="space-between" spacing="sm">
            <Typography variant="h5">Users</Typography>
            {!showNewUserForm ? (
              <Button
                variant="outlined"
                color="primary"
                onClick={handleShowUserForm}
                size="large"
              >
                Add user
              </Button>
            ) : null}
          </Row>
          {showNewUserForm ? (
            <NewUserForm
              vehicleId={vehicleId}
              vehicleApplicationId={vehicleApplicationId}
              onClose={() => {
                setShowNewUserForm(false);
              }}
              showCloseButton={hasUsers}
            />
          ) : null}
          <Table
            columns={columns}
            rows={users}
            emptyMessage="This investor has no users, click Add user button above to add one."
            actions={[
              {
                label: 'Make default user',
                icon: Cached,
                disabled: (user) => user.isDefaultUser || isLoading,
                onClick: (user) => {
                  handleChangeDefaultUser(user.id);
                },
              },
              {
                label: 'Remove user',
                icon: Delete,
                disabled: isLoading,
                onClick: (user) => {
                  removeUserDisclosure.stageAction({ userId: user.id });
                  removeUserDisclosure.onOpen();
                },
              },
            ]}
          />
        </Column>
        <ConfirmDialog
          open={removeUserDisclosure.isOpen}
          onClose={removeUserDisclosure.onClose}
          title="Remove user"
          content={
            <Typography variant="body2">
              Are you sure you want to remove the user from this investor? This
              action cannot be undone and will apply across your entire system,
              not just to this vehicle.
            </Typography>
          }
          actions={{
            confirm: {
              label: 'Remove',
              color: 'error',
              onClick: () => {
                removeUserDisclosure.onClose();
                handleRemoveUser({
                  vehicleApplicationId,
                  vehicleId,
                  userId: removeUserDisclosure.actionProps.userId,
                });
              },
            },
          }}
        />
      </CardContent>
    </Card>
  );
};

export default InvestorUsers;
